/**
 * `static_first_render` (canvas load optimization, task 0.3): render document content as static HTML
 * and create a TipTap editor only when a block is clicked or focused.
 *
 * Resolved once per page load (until rollout context changes), in this order:
 *   1. `VITE_STATIC_FIRST_RENDER=force-off` turns it off for everyone and clears stored overrides (the kill switch)
 *   -  a production-mode build (`--mode production`) skips URL/storage overrides: env, then rollout (below)
 *   2. `?static_first_render=1|0` applies to this page load only (non-production builds)
 *   3. the stored override (localStorage `notch.flag.static_first_render`)
 *   4. `VITE_STATIC_FIRST_RENDER` ('true' / 'false')
 *   5. legacy `VITE_STATIC_TABLE_CELLS=true` turns it on
 *   6. otherwise off
 *
 * Production rollout when step 4 is unset (3.6):
 *   a. `staticFirstRender` on subscription entitlements when the BE sends it
 *   b. else `VITE_STATIC_FIRST_RENDER_ROLLOUT_PERCENT` hash on `teamId` (stable 0–99 bucket)
 */

export const STATIC_FIRST_RENDER_PARAM = 'static_first_render'
export const STATIC_FIRST_RENDER_STORAGE_KEY = 'notch.flag.static_first_render'
export const STATIC_FIRST_RENDER_FORCE_OFF = 'force-off'
/** `?static_first_render=keep-on|keep-off` stores the override; a bare `1|0` lasts for one page load */
export const STATIC_FIRST_RENDER_KEEP_PREFIX = 'keep-'

export type StaticFirstRenderRollout = {
  teamId?: string
  /** From `GET /subscriptions/entitlements` when the field exists. */
  backendFlag?: boolean | null
}

type FlagEnv = Partial<
  Pick<
    ImportMetaEnv,
    'VITE_STATIC_FIRST_RENDER' | 'VITE_STATIC_TABLE_CELLS' | 'VITE_STATIC_FIRST_RENDER_ROLLOUT_PERCENT' | 'MODE'
  >
>
type FlagStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function parseBoolean(value: string | null | undefined): boolean | undefined {
  if (value === null || value === undefined) return undefined
  const normalized = value.trim().toLowerCase()
  if (['1', 'true', 'on'].includes(normalized)) return true
  if (['0', 'false', 'off'].includes(normalized)) return false
  return undefined
}

function readStorage(storage: FlagStorage | undefined): string | null {
  try {
    return storage?.getItem(STATIC_FIRST_RENDER_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

function writeStorage(storage: FlagStorage | undefined, value: boolean | null): void {
  try {
    if (value === null) storage?.removeItem(STATIC_FIRST_RENDER_STORAGE_KEY)
    else storage?.setItem(STATIC_FIRST_RENDER_STORAGE_KEY, String(value))
  } catch {
    // The override then applies to this page load only
  }
}

function parseRolloutPercent(env: FlagEnv): number {
  const raw = env.VITE_STATIC_FIRST_RENDER_ROLLOUT_PERCENT
  if (raw === undefined || raw === '') return 0
  const n = Number(raw.trim())
  if (!Number.isFinite(n)) return 0
  return Math.min(100, Math.max(0, Math.floor(n)))
}

/** Stable 0–99 bucket for percent rollout; same team id always gets the same bucket. */
export function staticFirstRenderRolloutBucket(teamId: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < teamId.length; i++) {
    hash ^= teamId.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0) % 100
}

export function teamIdInStaticFirstRenderRollout(teamId: string, percent: number): boolean {
  if (percent <= 0 || !teamId) return false
  if (percent >= 100) return true
  return staticFirstRenderRolloutBucket(teamId) < percent
}

function resolveProductionRollout(env: FlagEnv, rollout: StaticFirstRenderRollout): boolean {
  if (rollout.backendFlag === true) return true
  if (rollout.backendFlag === false) return false
  const percent = parseRolloutPercent(env)
  if (rollout.teamId) return teamIdInStaticFirstRenderRollout(rollout.teamId, percent)
  return false
}

let rolloutContext: StaticFirstRenderRollout = {}

export function setStaticFirstRenderRollout(ctx: StaticFirstRenderRollout): void {
  rolloutContext = { ...ctx }
  staticFirstRender = undefined
}

export function resetStaticFirstRenderRollout(): void {
  rolloutContext = {}
  staticFirstRender = undefined
}

export function resolveStaticFirstRender(
  search: string,
  storage: FlagStorage | undefined,
  env: FlagEnv,
  rollout: StaticFirstRenderRollout = rolloutContext
): boolean {
  const envValue = env.VITE_STATIC_FIRST_RENDER?.trim().toLowerCase()
  if (envValue === STATIC_FIRST_RENDER_FORCE_OFF) {
    writeStorage(storage, null)
    return false
  }
  if (env.MODE === 'production') {
    const fromEnv = parseBoolean(envValue)
    if (fromEnv !== undefined) return fromEnv
    return resolveProductionRollout(env, rollout)
  }

  const param = new URLSearchParams(search).get(STATIC_FIRST_RENDER_PARAM)?.trim().toLowerCase()
  if (param === 'reset') {
    writeStorage(storage, null)
  } else if (param?.startsWith(STATIC_FIRST_RENDER_KEEP_PREFIX)) {
    const kept = parseBoolean(param.slice(STATIC_FIRST_RENDER_KEEP_PREFIX.length))
    if (kept !== undefined) {
      writeStorage(storage, kept)
      return kept
    }
  } else {
    const fromParam = parseBoolean(param)
    if (fromParam !== undefined) return fromParam
  }

  const fromStorage = parseBoolean(readStorage(storage))
  if (fromStorage !== undefined) return fromStorage

  if (envValue) return parseBoolean(envValue) ?? false

  return env.VITE_STATIC_TABLE_CELLS?.trim().toLowerCase() === 'true'
}

function safeLocalStorage(): FlagStorage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

let staticFirstRender: boolean | undefined

export function isStaticFirstRender(): boolean {
  if (staticFirstRender === undefined) {
    const search = typeof window === 'undefined' ? '' : window.location.search
    staticFirstRender = resolveStaticFirstRender(search, safeLocalStorage(), import.meta.env)
  }
  return staticFirstRender
}
